import { useMemo } from "react";

import { useField } from "formik";
import PropTypes from "prop-types";

import { GrabsonIcon } from "components/layout";
import { classes } from "utils/classes";
import { getFieldTestId } from "utils/formik";

import { createMixedValidator } from ".";
import { Input } from "../elements";
import { FieldProps } from "./propTypes";
import styles from "./TextFieldWithQuery.scss";

export const TextFieldWithQuery = (props) => {
  const {
    name,
    required = true,
    query,
    dataTestId,
    validate: validateFn,
  } = props;

  const validate = useMemo(
    () => createMixedValidator(name, required, validateFn),
    [name, required, validateFn]
  );

  const [field, meta] = useField({
    ...props,
    required,
    type: "text",
    validate,
  });

  const error = useMemo(() => {
    if (query.pending) {
      return undefined;
    }
    return query.error || meta.error;
  }, [query, meta.error]);

  const renderIcon = () => {
    if (!meta.touched) {
      return null;
    }
    if (query.pending) {
      return (
        <GrabsonIcon
          className={classes(styles.icon, styles.pending)}
          name="pending"
          size="xl"
        />
      );
    }
    if (error) {
      return (
        <GrabsonIcon
          className={classes(styles.icon, styles.error)}
          name="close"
          size="xl"
        />
      );
    }
    return (
      <GrabsonIcon
        className={classes(styles.icon, styles.success)}
        name="ok"
        size="xl"
      />
    );
  };

  return (
    <Input
      {...meta}
      {...props}
      className={styles.container}
      error={error}
      props={field}
      required={required}
      dataTestId={dataTestId || getFieldTestId(name)}
    >
      {renderIcon()}
    </Input>
  );
};

TextFieldWithQuery.propTypes = {
  ...FieldProps,
  query: PropTypes.shape({
    error: PropTypes.string,
    pending: PropTypes.bool.isRequired,
  }).isRequired,
};
